/**
 * Corpus Status Service
 * Centralized service for managing corpus status logic
 */

// Backend enum statuses
export const CORPUS_STATUS = {
  UNSPECIFIED: 'UNSPECIFIED',
  PENDING: 'PENDING',
  RUNNING: 'RUNNING',
  COMPLETED: 'COMPLETED',
  UPDATING: 'UPDATING',
  FAILED: 'FAILED',
  CANCELLING: 'CANCELLING',
  CANCELLED: 'CANCELLED',
  DELETING: 'DELETING',
  DELETED: 'DELETED',
  STUCK: 'STUCK',
  DAMAGED: 'DAMAGED',
  // Legacy statuses for backward compatibility
  FAIL: 'FAILED',
  UNKNOWN: 'UNSPECIFIED',
};

const PROCESSING_STATUSES = [
  CORPUS_STATUS.UNSPECIFIED,
  CORPUS_STATUS.PENDING,
  CORPUS_STATUS.RUNNING,
  CORPUS_STATUS.UPDATING,
  CORPUS_STATUS.CANCELLING,
  CORPUS_STATUS.DELETING,
];

const ERROR_STATUSES = [
  CORPUS_STATUS.FAILED,
  CORPUS_STATUS.CANCELLED,
  CORPUS_STATUS.STUCK,
  CORPUS_STATUS.DAMAGED,
];

export class CorpusStatusService {
  /**
   * Normalize backend status to a canonical value
   * @param {string} status - Raw status from backend
   * @returns {string} Normalized status
   */
  static normalizeStatus(status) {
    if (!status) {
      return CORPUS_STATUS.UNSPECIFIED;
    }

    const normalizedStatus = status.toUpperCase();

    if (normalizedStatus === 'FAIL') {
      return CORPUS_STATUS.FAILED;
    }

    if (normalizedStatus === 'UNKNOWN') {
      return CORPUS_STATUS.UNSPECIFIED;
    }

    return normalizedStatus;
  }

  /**
   * Check if status is valid
   * @param {string} status - The status value
   * @returns {boolean} Whether status is valid
   */
  static isValidStatus(status) {
    const normalizedStatus = this.normalizeStatus(status);
    return Object.values(CORPUS_STATUS).includes(normalizedStatus);
  }

  /**
   * Get default status for new corpus
   * @returns {string} Default status
   */
  static getDefaultStatus() {
    return CORPUS_STATUS.PENDING;
  }

  /**
   * Check if provided status is considered a processing state
   * @param {string} status - Status to evaluate
   * @returns {boolean} Whether status is processing
   */
  static isProcessingStatus(status) {
    const normalizedStatus = this.normalizeStatus(status);
    return PROCESSING_STATUSES.includes(normalizedStatus);
  }

  /**
   * Check if provided status is considered an error state
   * @param {string} status - Status to evaluate
   * @returns {boolean} Whether status is error
   */
  static isErrorStatus(status) {
    const normalizedStatus = this.normalizeStatus(status);
    return ERROR_STATUSES.includes(normalizedStatus);
  }
}
